import { OPRAEQParameters, OPRAEQBand } from "@/types/opra"
import { DEFAULT_SETTINGS } from "@/settings"

// Convert OPRA parametric EQ to your app's config format
export const convertOPRAToConfig = (opraParams: OPRAEQParameters): object => {
  const config: any = {
    Preamp: {
      Mode: {
        Elements: ["Auto", "Manual"],
        Current: "Auto"
      },
      Gain: {
        Min: -30,
        Max: 30,
        Current: opraParams.gain_db || 0,
      }
    }
  }

  // Get default DSP settings for Headphone output to use as templates
  const defaultDSP = DEFAULT_SETTINGS.Configuration.DSP.Headphone

  // Define all 12 bands in the correct order
  const bandOrder = [
    "Lowshelf 1",
    "Lowshelf 2", 
    "Peaking 1",
    "Peaking 2",
    "Peaking 3",
    "Peaking 4",
    "Peaking 5",
    "Peaking 6",
    "Peaking 7",
    "Peaking 8",
    "Highshelf 1",
    "Highshelf 2",
  ]

  // Initialize all 12 bands with default values (Gain = 0)
  bandOrder.forEach((bandName) => {
    const defaultBand = defaultDSP[bandName]
    if (defaultBand) {
      config[bandName] = {
        Gain: {
          Min: -30,
          Max: 30,
          Current: 0, // Start with 0 gain for unused bands
        },
        Frequency: {
          Min: 20,
          Max: 20000,
          Current: defaultBand.Frequency.Current, // Use default frequency
        },
        Q: {
          Min: 0.1,
          Max: defaultBand.Q.Max, // Use correct max Q for the band type
          Current: 0.707, // Default Q value
        },
        Type: {
          Elements: ["LOWSHELF", "PEAKING", "HIGHSHELF"],
          Current: defaultBand.Type.Current, // Use default type for the band
        }
      }
    }
  })

  // Track which bands we've used for OPRA data
  let lowshelfIndex = 0
  let highshelfIndex = 0  
  let peakingIndex = 0

  // Now apply OPRA bands to available slots
  opraParams.bands.forEach((band: OPRAEQBand) => {
    // Map OPRA filter type to our app's format
    const filterType = mapOPRAFilterType(band.type)
    
    // Determine which band slot to use based on type
    let targetBandName: string | null = null
    
    if (filterType === "LOWSHELF") {
      if (lowshelfIndex < 2) { // We have 2 lowshelf bands
        targetBandName = `Lowshelf ${lowshelfIndex + 1}`
        lowshelfIndex++
      }
    } else if (filterType === "HIGHSHELF") {
      if (highshelfIndex < 2) { // We have 2 highshelf bands
        targetBandName = `Highshelf ${highshelfIndex + 1}`
        highshelfIndex++
      }
    } else { // PEAKING
      if (peakingIndex < 8) { // We have 8 peaking bands
        targetBandName = `Peaking ${peakingIndex + 1}`
        peakingIndex++
      }
    }
    
    // If we found a slot, apply the OPRA band data
    if (targetBandName && config[targetBandName]) {
      config[targetBandName].Gain.Current = band.gain_db
      config[targetBandName].Frequency.Current = Math.round(band.frequency)
      config[targetBandName].Q.Current = Math.round(band.q * 1000) / 1000 // Round to 3 decimal places
      config[targetBandName].Type.Current = filterType
      
      // Update Q max based on filter type
      config[targetBandName].Q.Max = filterType === "PEAKING" ? 50 : 3
      
      console.debug(`Applied OPRA band to ${targetBandName}: ${filterType} @ ${Math.round(band.frequency)}Hz, ${band.gain_db}dB, Q=${band.q}`)
    } else {
      console.warn(`Could not find available slot for ${filterType} filter (freq: ${band.frequency}Hz, gain: ${band.gain_db}dB)`)
    }
  })

  console.debug(`OPRA converter created config with all 12 bands. Used: ${lowshelfIndex} lowshelf, ${highshelfIndex} highshelf, ${peakingIndex} peaking bands`)

  return config
}

// Convert your app's config format to OPRA parametric EQ
export const convertConfigToOPRA = (config: any): OPRAEQParameters => {
  return {
    gain_db: config.preamp || 0,
    bands: (config.bands || []).map((band: any) => ({
      type: mapConfigFilterType(band.type),
      frequency: band.frequency,
      gain_db: band.gain,
      q: band.q,
    })),
  }
}

// Map OPRA filter types to your app's filter types
const mapOPRAFilterType = (opraType: string): string => {
  const typeMap: Record<string, string> = {
    low_shelf: "LOWSHELF",
    high_shelf: "HIGHSHELF",
    peak_dip: "PEAKING",
    notch: "PEAKING", // Treat notch as peaking
    low_pass: "PEAKING", // Treat low pass as peaking  
    high_pass: "PEAKING", // Treat high pass as peaking
  }
  return typeMap[opraType] || "PEAKING"
}

// Map your app's filter types to OPRA filter types
const mapConfigFilterType = (configType: string): string => {
  const typeMap: Record<string, string> = {
    LOWSHELF: "low_shelf",
    HIGHSHELF: "high_shelf", 
    PEAKING: "peak_dip",
    lowShelf: "low_shelf", // Legacy support
    highShelf: "high_shelf", // Legacy support
    peaking: "peak_dip", // Legacy support
    notch: "notch",
    lowPass: "low_pass",
    highPass: "high_pass",
  }
  return typeMap[configType] || "peak_dip"
}

// Validate OPRA EQ parameters
export const validateOPRAParameters = (params: OPRAEQParameters): boolean => {
  try {
    if (typeof params.gain_db !== "number") return false
    if (!Array.isArray(params.bands)) return false
    
    return params.bands.every((band) => 
      typeof band.frequency === "number" &&
      typeof band.gain_db === "number" &&
      typeof band.q === "number" &&
      typeof band.type === "string"
    )
  } catch {
    return false
  }
}

// Create a unified preset format that works with both systems
export const createUnifiedPreset = (preset: any) => {
  const isOPRAPreset = preset.source === "opra"
  
  return {
    ...preset,
    config: isOPRAPreset ? convertOPRAToConfig(preset.config) : preset.config,
    // Add metadata to distinguish source
    metadata: {
      source: preset.source || "cloud",
      originalFormat: isOPRAPreset ? "opra" : "cloud",
      ...(isOPRAPreset && {
        author: preset.author,
        details: preset.details,
      })
    }
  }
} 